import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

function generateSessionId() {
  return "chat-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello! I'm BBE's AI Property Assistant. How can I help you find the right property in Bengaluru?" },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(generateSessionId());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => { scrollBottom(); }, [messages, scrollBottom]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setStreaming(true);

    // Add empty assistant message for streaming
    setMessages((prev) => [...prev, { role: "assistant", text: "", streaming: true }]);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                  updated[updated.length - 1] = { ...last, text: last.text + data.content };
                }
                return updated;
              });
            } else if (data.type === "error") {
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", text: data.content };
                return updated;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", text: "Sorry, I'm having trouble connecting. Please try again." };
        return updated;
      });
    }

    setMessages((prev) => prev.map((m) => ({ ...m, streaming: false })));
    setStreaming(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* Toggle Button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-[100] w-14 h-14 bg-gradient-to-br from-[#E7CB6A] via-[#D4AF37] to-[#B8952E] text-[#0A2342] flex items-center justify-center shadow-lg shadow-[#D4AF37]/30 hover:shadow-[#D4AF37]/50 transition-shadow"
            data-testid="chat-toggle"
          >
            <MessageCircle size={24} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-6 right-6 z-[100] w-[380px] max-w-[calc(100vw-48px)] h-[520px] max-h-[calc(100vh-120px)] bg-[#0A2342] border border-[#EDE6D3]/15 flex flex-col shadow-2xl"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE6D3]/12">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-[#E7CB6A] to-[#B8952E] flex items-center justify-center">
                  <MessageCircle size={16} color="#0A2342" />
                </div>
                <div>
                  <div className="text-[12px] tracking-[2px] uppercase font-medium">BBE Assistant</div>
                  <div className="text-[10px] text-[#D4AF37] tracking-[1px]">AI-Powered</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-[#EDE6D3]/10 transition-colors" data-testid="chat-close">
                <X size={16} color="#EDE6D3" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-[13px] leading-[1.7] ${
                      m.role === "user"
                        ? "bg-[#D4AF37] text-[#0A2342]"
                        : "bg-[#0E2A4E] text-[#EDE6D3]/90 border border-[#EDE6D3]/10"
                    }`}
                    data-testid={`chat-msg-${m.role}-${i}`}
                  >
                    {m.text || (m.streaming ? <Loader2 size={14} className="animate-spin text-[#D4AF37]" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="border-t border-[#EDE6D3]/12 px-4 py-3 flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about properties..."
                className="flex-1 bg-transparent border-0 text-[13px] text-[#EDE6D3] placeholder:text-[#EDE6D3]/35 outline-none"
                disabled={streaming}
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || streaming}
                className="p-2 bg-[#D4AF37] text-[#0A2342] disabled:opacity-30 hover:bg-[#E7CB6A] transition-colors"
                data-testid="chat-send"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
